/**
 * G1. Детство 3–6: «Двор». Поле 3×3 с соседскими детьми; тап по ребёнку —
 * «позвать играть», он соглашается с p по отношениям. Три тапа (по разным
 * детям). Друзей 0…3 → отношения +0…+4 (0 → 0, n → n + 1); запись
 * журнала `minigame.yard.friends.<n>` — первая строка «друг».
 * «Пропустить» — ожидаемое число друзей round(3·p).
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { distinctIndexes, nothing } from './common';

export const YARD_MIN_AGE = 3;
export const YARD_MAX_AGE = 6;
export const YARD_CELLS = 9;
export const YARD_TAPS = 3;
/** p согласия = база + за очко отношений: 0 → 0,2, 100 → 0,8. */
export const YARD_P_BASE = 0.2;
export const YARD_P_PER_POINT = 0.006;

export interface YardState {
  cells: number;
  taps: number;
  /** Вероятность, что позванный ребёнок согласится. */
  p: number;
}

export type YardInput = { taps: number[] };

export function yardAcceptP(relations: number): number {
  const r = Number.isFinite(relations) ? Math.min(100, Math.max(0, relations)) : 0;
  return YARD_P_BASE + YARD_P_PER_POINT * r;
}

/** Отношения за число друзей: 0 → 0, n → n + 1 (максимум +4). */
export function yardRelations(friends: number): number {
  return friends <= 0 ? 0 : Math.min(YARD_TAPS, friends) + 1;
}

function result(friends: number): MinigameOutcome {
  return { ...nothing(), stats: { relations: yardRelations(friends) }, logKey: `minigame.yard.friends.${friends}`, logParams: { friends } };
}

export const yard: MinigameDef<YardInput, YardState> = {
  id: 'yard',
  section: 'G',
  triggers: [{ kind: 'age', min: YARD_MIN_AGE, max: YARD_MAX_AGE, everyYears: 1 }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return life.age >= YARD_MIN_AGE && life.age <= YARD_MAX_AGE;
  },
  setup(life: Life): YardState {
    return { cells: YARD_CELLS, taps: YARD_TAPS, p: yardAcceptP(life.stats.relations) };
  },
  play(state: YardState, input: YardInput, _life: Life, rng: Rng): MinigameOutcome {
    const taps = distinctIndexes(input.taps, state.cells).slice(0, state.taps);
    let friends = 0;
    for (let i = 0; i < taps.length; i += 1) if (rng.chance(state.p)) friends += 1;
    return result(friends);
  },
  expected(state: YardState): MinigameOutcome {
    return result(Math.round(state.taps * state.p));
  },
};
